import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ComponentPropsWithRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './icon';
import styles from './input.module.css';

type FieldProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: ReactElement<{
    id?: string;
    name?: string;
    required?: boolean;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
    'data-state'?: string;
  }>;
};

export function Field({
  label,
  helper,
  error,
  required,
  htmlFor,
  className,
  children,
  ...rest
}: FieldProps) {
  const autoId = useId();
  const id = htmlFor || autoId;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : helper ? helperId : undefined;

  const child = Children.only(children);
  const control = isValidElement(child)
    ? cloneElement(child, {
        id,
        name: child.props.name || id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'data-state': error ? 'error' : child.props['data-state'],
        required,
      })
    : child;

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')} {...rest}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true"> *</span>
          )}
        </label>
      )}
      {control}
      {error ? (
        <p id={errorId} role="alert" className={`${styles.msg} ${styles.msgError}`}>
          {error}
        </p>
      ) : helper ? (
        <p id={helperId} className={styles.msg}>
          {helper}
        </p>
      ) : null}
    </div>
  );
}

type InputProps = ComponentPropsWithRef<'input'> & {
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  onTrailingClick?: () => void;
  trailingAriaLabel?: string;
};

export function Input({
  leadingIcon,
  trailingIcon,
  onTrailingClick,
  trailingAriaLabel,
  className,
  ref,
  ...rest
}: InputProps) {
  const wrapClasses = [
    styles.inputWrap,
    leadingIcon ? styles.hasLeading : null,
    trailingIcon ? styles.hasTrailing : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wrapClasses}>
      {leadingIcon ? (
        <Icon name={leadingIcon} size={16} className={styles.leading} aria-hidden="true" />
      ) : null}
      <input ref={ref} className={[styles.input, className].filter(Boolean).join(' ')} {...rest} />
      {trailingIcon ? (
        <button
          type="button"
          className={styles.trailing}
          onClick={onTrailingClick}
          // eslint-disable-next-line i18next/no-literal-string -- fallback string for misuse: every Input with trailingIcon should pass trailingAriaLabel
          aria-label={trailingAriaLabel || 'Action'}
          tabIndex={-1}
        >
          <Icon name={trailingIcon} size={16} aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}

type TextareaProps = ComponentPropsWithRef<'textarea'>;

export function Textarea({ className, ref, ...rest }: TextareaProps) {
  return (
    <textarea
      ref={ref}
      className={[styles.input, styles.textarea, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
}

type SelectProps = ComponentPropsWithRef<'select'> & {
  /** Shrink-to-content width for inline/toolbar use (default: full width, for form fields). */
  inline?: boolean;
};

export function Select({ className, children, inline, ref, ...rest }: SelectProps) {
  return (
    <span className={[styles.selectWrap, inline ? styles.selectWrapInline : null].filter(Boolean).join(' ')}>
      <select
        ref={ref}
        className={[styles.input, styles.select, className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </select>
      <Icon
        name="chevron-down"
        size={16}
        className={styles.selectChevron}
        aria-hidden="true"
      />
    </span>
  );
}
